import { Injectable } from '@angular/core';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { SharedService } from '../../shared/_services/shared.service';

@Injectable({
  providedIn: 'root',
})
export class VisitorService {
  private readonly TRIAL_KEY = 'fpjs_trial_data';

  /**
   * Last-resort trial length, used ONLY by fallbackTrialLogic when the guest
   * API could not be reached.
   *
   * The real trial length lives on the hidden 'trial' plan document in
   * subscriptionplansv1 and reaches us through v1/guest/identify, which returns
   * the already-resolved plan and expiry. This constant is not that value and
   * will not follow it — change the document, not this number.
   */
  private readonly FALLBACK_TRIAL_DURATION_DAYS = 2;

  private subscriptionPlan: string | null = null;
  private subscriptionExpiresAt: number | null = null;
  /**
   * The backend's own expiry verdict from v1/guest/identify. Preferred over
   * comparing the timestamp here, so the trial's cut-off is decided in one
   * place — the server — rather than by two clocks that can disagree.
   * Null when the guest API has not answered.
   */
  private subscriptionExpired: boolean | null = null;

  private visitorIdCache: string | null = null;
  private initialized = false;
  private initializationPromise: Promise<void> | null = null;

  constructor(private sharedService: SharedService) { }

  private async initSubscriptionInfo(): Promise<void> {
    if (this.initialized) return;

    if (this.initializationPromise) {
      await this.initializationPromise;
      return;
    }

    this.initializationPromise = this.initializeVisitorInfo()
    await this.initializationPromise;
  }

  private async initializeVisitorInfo(): Promise<void> {
    const visitorId = await this.getVisitorId();

    if (visitorId === 'fallback-id' || visitorId === 'ssr-fallback-id') {
      // console.warn('Could not identify visitor properly. Skipping trial tracking.');
      this.initialized = true;
      return;
    }

    await this.identifyGuest(visitorId);
    this.initialized = true;
  }

  public async getTrialStatus(): Promise<'trial' | 'advance' | 'none'> {
    await this.initSubscriptionInfo();
    if (this.subscriptionPlan && this.subscriptionExpiresAt) {
      if (this.hasExpired()) {
        return 'none';
      }
      // Whatever the guest API granted — 'trial' now, 'advance' for guests
      // identified before TRIAL became its own plan.
      return this.subscriptionPlan.toLowerCase() as 'trial' | 'advance';
    }

    return this.fallbackTrialLogic();
  }

  /* @@ AI-COMMENT: Calculate days left, returning null if subscription data isn't loaded yet */
  public async getSubscriptionInfo(): Promise<{ plan: string; daysLeft: number | null; isTrial: boolean }> {
    await this.initSubscriptionInfo();

    const now = Date.now();
    const plan = this.subscriptionPlan || 'none';

    // The guest/fingerprint path is always the free trial, never a purchased plan.
    if (this.subscriptionExpiresAt === null) {
      return { plan, daysLeft: null, isTrial: true };
    }

    // A lapsed trial grants nothing, so report 'none' rather than the plan the
    // guest record still names. PlanAccessService feeds this straight into the
    // feature matrix — returning "TRIAL" here left an expired visitor with the
    // full advance-level feature set.
    if (this.hasExpired()) {
      return { plan: 'none', daysLeft: 0, isTrial: true };
    }

    let expiryTime = Number(this.subscriptionExpiresAt);
    
    // Normalize: If the timestamp is in seconds (10 digits), convert to ms (13 digits)
    if (expiryTime > 0 && expiryTime < 10000000000) {
      expiryTime *= 1000;
    }

    // Not expired by this point — the lapsed case returned above.
    const daysLeft = Math.max(Math.ceil((expiryTime - now) / (1000 * 60 * 60 * 24)), 0);

    console.log(`[VisitorSubscriptionCheck] Expiry: ${expiryTime}, Now: ${now}, DaysLeft: ${daysLeft}, Plan: ${plan}`);

    return { plan, daysLeft, isTrial: true };
  }

  /**
   * Offline approximation of the trial, for when fingerprinting or the guest
   * API failed. Both the length and the granted tier are fixed defaults here,
   * so this can disagree with the trial plan document — it only runs when the
   * authoritative answer was unreachable.
   */
  private async fallbackTrialLogic(): Promise<'trial' | 'none'> {
    const visitorId = await this.getVisitorId();

    let data: Record<string, { startedAt: string }> = {};
    const raw = localStorage.getItem(this.TRIAL_KEY);
    if (raw) {
      try {
        data = JSON.parse(raw);
      } catch {
        console.warn('Corrupt trial data, resetting...');
        localStorage.removeItem(this.TRIAL_KEY);
      }
    }

    if (!data[visitorId]) {
      data[visitorId] = { startedAt: new Date().toISOString() };
      localStorage.setItem(this.TRIAL_KEY, JSON.stringify(data));
    }

    const startedAt = data[visitorId]?.startedAt;
    const startTime = new Date(startedAt).getTime();
    const now = Date.now();

    if (isNaN(startTime)) {
      console.warn('Invalid start time detected. Resetting trial.');
      data[visitorId] = { startedAt: new Date().toISOString() };
      localStorage.setItem(this.TRIAL_KEY, JSON.stringify(data));
      return 'trial';
    }

    const daysPassed = Math.floor((now - startTime) / (1000 * 60 * 60 * 24));
    return daysPassed < this.FALLBACK_TRIAL_DURATION_DAYS ? 'trial' : 'none';
  }

  /**
   * Whether the guest trial has lapsed. Trusts the API's isExpired when it sent
   * one, and falls back to comparing the timestamp against the local clock.
   */
  private hasExpired(): boolean {
    if (this.subscriptionExpired !== null) {
      return this.subscriptionExpired;
    }
    return this.subscriptionExpiresAt !== null && this.subscriptionExpiresAt <= Date.now();
  }

  private async identifyGuest(visitorId: string): Promise<boolean> {
    try {
      return await new Promise<boolean>((resolve) => {
        this.sharedService.guestIdentify(visitorId).subscribe({
          next: (res: any) => {
            const data = res?.data;
            if (data?.subscriptionPlan && data?.subscriptionExpiresAt) {
              this.subscriptionPlan = data.subscriptionPlan;
              this.subscriptionExpiresAt = data.subscriptionExpiresAt;
              // Older API builds do not send isExpired; leave it null so the
              // timestamp comparison below still decides.
              this.subscriptionExpired = typeof data.isExpired === 'boolean'
                ? data.isExpired
                : null;
              resolve(true);
            } else {
              resolve(false);
            }
          },
          error: (err: any) => {
            console.error('identifyGuest API error', err);
            resolve(false);
          },
        });
      });
    } catch (e) {
      console.error('Unexpected error in identifyGuest:', e);
      return false;
    }
  }

  public async getVisitorId(): Promise<string> {
    if (this.visitorIdCache) return this.visitorIdCache;

    if (typeof window === 'undefined') return 'ssr-fallback-id';

    try {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      this.visitorIdCache = result.visitorId;
      return result.visitorId;
    } catch (error) {
      console.error('FingerprintJS failed to load:', error);
      return 'fallback-id';
    }
  }
}